/**
 * PerformanceSettingsPanel Component
 * Admin panel for configuring performance score weights
 */

import React, { useState, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { Card } from '../../common';
import { Settings, Save, RotateCcw, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { usePerformanceWeights, useUpdatePerformanceWeights } from '../../../hooks';
import type { PerformanceWeights } from '../../../types';
import { DEFAULT_PERFORMANCE_WEIGHTS } from '../../../types';

export interface PerformanceSettingsPanelProps {
  className?: string;
}

interface WeightConfig {
  key: keyof PerformanceWeights;
  label: string;
  description: string;
  color: string;
}

const WEIGHT_CONFIGS: WeightConfig[] = [
  {
    key: 'okrProgress',
    label: 'OKR Progress',
    description: 'Measures completion and progress of assigned objectives and key results',
    color: 'bg-blue-500',
  },
  {
    key: 'checkInCompliance',
    label: 'Check-in Compliance',
    description: 'Tracks regularity and timeliness of progress check-ins',
    color: 'bg-purple-500',
  },
  {
    key: 'engagement',
    label: 'Engagement',
    description: 'Measures collaboration through comments and interactions',
    color: 'bg-teal-500',
  },
  {
    key: 'timeliness',
    label: 'Timeliness',
    description: 'Evaluates on-time completion and updates',
    color: 'bg-amber-500',
  },
];

export const PerformanceSettingsPanel: React.FC<PerformanceSettingsPanelProps> = ({
  className,
}) => {
  const { data: currentWeights, isLoading } = usePerformanceWeights();
  const updateWeights = useUpdatePerformanceWeights();

  const [weights, setWeights] = useState<PerformanceWeights>(DEFAULT_PERFORMANCE_WEIGHTS);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize weights from server data
  useEffect(() => {
    if (currentWeights) {
      setWeights(currentWeights);
    }
  }, [currentWeights]);

  // Calculate total weight
  const totalWeight = useMemo(() => {
    return Object.values(weights).reduce((sum, w) => sum + w, 0);
  }, [weights]);

  // Check if total is valid (should equal 1.0 / 100%)
  const isValidTotal = useMemo(() => {
    return Math.abs(totalWeight - 1.0) < 0.001;
  }, [totalWeight]);

  // Check if there are unsaved changes
  useEffect(() => {
    if (!currentWeights) return;
    const changed = WEIGHT_CONFIGS.some(
      (config) => weights[config.key] !== currentWeights[config.key]
    );
    setHasChanges(changed);
  }, [weights, currentWeights]);

  const handleWeightChange = (key: keyof PerformanceWeights, value: number) => {
    // Convert percentage (0-100) to decimal (0-1)
    const decimalValue = Math.max(0, Math.min(100, value)) / 100;
    setWeights((prev) => ({
      ...prev,
      [key]: decimalValue,
    }));
  };

  const handleReset = () => {
    setWeights(DEFAULT_PERFORMANCE_WEIGHTS);
  };

  const handleSave = async () => {
    if (!isValidTotal) return;

    try {
      await updateWeights.mutateAsync(weights);
    } catch (error) {
      console.error('Failed to save weights:', error);
    }
  };

  // Calculate a sample score preview
  const sampleScore = useMemo(() => {
    const sampleMetrics = {
      okrProgress: 75,
      checkInCompliance: 80,
      engagement: 60,
      timeliness: 90,
    };

    return (
      sampleMetrics.okrProgress * weights.okrProgress +
      sampleMetrics.checkInCompliance * weights.checkInCompliance +
      sampleMetrics.engagement * weights.engagement +
      sampleMetrics.timeliness * weights.timeliness
    );
  }, [weights]);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-48 mb-6" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="mb-6">
              <div className="h-4 bg-stone-200 rounded w-32 mb-2" />
              <div className="h-10 bg-stone-100 rounded" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Settings className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-stone-800">Performance Weights</h3>
            <p className="text-sm text-stone-500">Configure how performance scores are calculated</p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">How Weights Work</p>
          <p>
            Each factor contributes to the overall performance score based on its weight.
            Weights must sum to 100%. Changes will affect all future performance calculations.
          </p>
        </div>
      </div>

      {/* Weight Sliders - 2x2 Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {WEIGHT_CONFIGS.map((config) => (
          <div key={config.key} className="p-4 bg-stone-50 rounded-lg border border-stone-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={clsx('w-3 h-3 rounded-full', config.color)} />
                <label className="font-medium text-stone-700 text-sm">{config.label}</label>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={Math.round(weights[config.key] * 100)}
                  onChange={(e) =>
                    handleWeightChange(config.key, parseInt(e.target.value) || 0)
                  }
                  className="w-14 px-2 py-1 text-right border border-stone-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
                <span className="text-sm text-stone-500">%</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(weights[config.key] * 100)}
              onChange={(e) => handleWeightChange(config.key, parseInt(e.target.value))}
              className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <p className="mt-2 text-xs text-stone-500">{config.description}</p>
          </div>
        ))}
      </div>

      {/* Total + Preview + Actions — compact bar */}
      <div className="mt-5 pt-5 border-t border-stone-200 flex flex-wrap items-center justify-between gap-4">
        {/* Total Weight */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-stone-600">Total:</span>
          <span
            className={clsx(
              'text-lg font-bold',
              isValidTotal ? 'text-green-600' : 'text-red-600'
            )}
          >
            {Math.round(totalWeight * 100)}%
          </span>
          {isValidTotal ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-500" />
          )}
          {!isValidTotal && (
            <span className="text-xs text-red-600">Must equal 100%</span>
          )}
        </div>

        {/* Preview */}
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <span>Preview:</span>
          <span className="font-semibold text-stone-700">{sampleScore.toFixed(1)}</span>
          <span>/100</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>

          <button
            onClick={handleSave}
            disabled={!hasChanges || !isValidTotal || updateWeights.isPending}
            className={clsx(
              'flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors',
              hasChanges && isValidTotal && !updateWeights.isPending
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            )}
          >
            {updateWeights.isPending ? (
              <>
                <span className="animate-spin">⏳</span>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {updateWeights.isSuccess && (
        <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          <span className="text-sm">Performance weights updated successfully!</span>
        </div>
      )}

      {updateWeights.isError && (
        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">Failed to update weights. Please try again.</span>
        </div>
      )}
    </Card>
  );
};

export default PerformanceSettingsPanel;
